import React, { useCallback, useEffect, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  isLicenseExpiredPayload,
  markLicenseExpiredAndRedirect,
  isLicenseExpiredFlagSet,
} from "../../utils/licenseExpired";
import { useDeviceIdentity } from "../../hooks/useDeviceIdentity";
import { useBackgroundDeviceHeartbeat } from "../../hooks/useBackgroundDeviceHeartbeat";

const ProtectedRoute = ({ children }) => {
  const user = useSelector((state) => state.auth.user);
  const token = useSelector((state) => state.auth.token);
  useDeviceIdentity(true);
  useBackgroundDeviceHeartbeat(true);

  const baseUrl = useMemo(
    () =>
      (process.env.REACT_APP_API_BASE_URL || "http://localhost:50268").replace(
        /\/+$/,
        "",
      ),
    [],
  );

  const getAuthHeaders = useCallback(() => {
    const deviceToken =
      localStorage.getItem("edgeDeviceToken") ||
      process.env.REACT_APP_DEVICE_TOKEN ||
      "";

    return {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(deviceToken ? { "X-DEVICE-TOKEN": deviceToken } : {}),
    };
  }, [token]);

  const buildCheckUrl = useCallback(() => {
    const params = new URLSearchParams();
    params.append("since", new Date(0).toISOString());
    params.append("scope", "STORE");
    const storeId = user?.storeId || localStorage.getItem("selectedStoreId");
    if (storeId) params.append("storeId", storeId);
    return `${baseUrl}/api/edge/realtime/sync?${params.toString()}`;
  }, [baseUrl, user?.storeId]);

const checkLicense = useCallback(async () => {
    if (isLicenseExpiredFlagSet()) {
      markLicenseExpiredAndRedirect();
      return;
    }

    try {
      const response = await fetch(buildCheckUrl(), {
        method: "GET",
        headers: getAuthHeaders(),
      });

      const contentType = response.headers.get("content-type") || "";
      let payload = null;
      if (contentType.includes("application/json")) {
        payload = await response.json();
      }

      if (payload && isLicenseExpiredPayload(payload)) {
        markLicenseExpiredAndRedirect();
        return;
      }
    } catch {
      // Ignore transient errors; user stays in app if check fails.
    }
  }, [
    buildCheckUrl,
    getAuthHeaders,
    isLicenseExpiredFlagSet,
    markLicenseExpiredAndRedirect,
  ]);

  useEffect(() => {
    let isMounted = true;
    const runCheck = async () => {
      await checkLicense();
      if (!isMounted) return;
    };
    runCheck();
    return () => {
      isMounted = false;
    };
  }, [checkLicense]);

  // If not logged in, redirect to login
  if (!token || !user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

export default ProtectedRoute;
